/**
 * One body-scroll lock, shared by everything that needs one.
 *
 * ## Why this is a module and not a few lines inside the component
 *
 * The obvious version — remember `body.style.overflow`, set it to `hidden`,
 * put it back on close — is wrong the moment two of them overlap, and this app
 * overlaps them constantly: a dozen pages mount more than one `<EditModal>`,
 * and the leads page mounts two bound to store flags that other pages can set.
 *
 * Written inside `<script setup>` it is worse than wrong, it is *invisibly*
 * wrong: every top-level binding in `<script setup>` is per-INSTANCE, because
 * that block is the setup function body. A `let openCount = 0` there reads like
 * shared state and is nothing of the sort. Each drawer then keeps a private
 * count and a private idea of what the original overflow was:
 *
 *   drawer A opens  → A records "" , body := hidden
 *   drawer B opens  → B records "hidden" (!), body := hidden
 *   drawer A closes → body := ""      … while B is still open
 *   drawer B closes → body := "hidden" … and the page can never scroll again
 *
 * That last line is the bug this file exists to remove: the page looks normal,
 * the mouse wheel does nothing, and a refresh "fixes" it — because a refresh is
 * the only thing that resets the inline style.
 *
 * These bindings are at real module scope, so every caller shares one count and
 * one memory of the original value. The first lock records it; only the last
 * release restores it.
 */

let lockCount = 0;
let previousOverflow: string | null = null;

/** Take a lock. Returns the release function; calling it twice is a no-op. */
export function lockBodyScroll(): () => void {
  if (typeof document === "undefined") return () => {};

  if (lockCount === 0) {
    // Read once, on the way in. Reading it again later would capture `hidden`,
    // which is the value this very lock wrote.
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }

  lockCount += 1;

  let released = false;

  return () => {
    // Guarded because the callers that need this most — a component that both
    // closes and unmounts — would otherwise release twice and let a still-open
    // drawer's lock go with it.
    if (released) return;
    released = true;

    lockCount = Math.max(0, lockCount - 1);

    if (lockCount === 0) {
      document.body.style.overflow = previousOverflow ?? "";
      previousOverflow = null;
    }
  };
}

/**
 * Escape hatch for a page that finds itself locked with nothing on screen.
 *
 * Not used in the normal flow and not a substitute for releasing properly —
 * it is here so a genuine leak can be cleared without a full reload while its
 * cause is being found.
 */
export function forceReleaseBodyScroll(): void {
  if (typeof document === "undefined") return;
  lockCount = 0;
  previousOverflow = null;
  document.body.style.overflow = "";
}
